import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { ProgressColourPalette } from "@app/directives/_misc/v-progress-bar/v-progress-bar.component";
import { IGoogleMapsMarker } from "@app/interfaces/IGoogleMapsMarkerOptions";
import { IPeripheral } from "@app/interfaces/IPeripheral";
import { IVehicleWithCameraMappings } from "@app/main/components/resourceManagement/cameras/camera-vehicles-manager/camera-vehicles-manager.component";
import { CameraChannelType } from "@app/main/components/videos/enums/CameraChannelType";
import { ICameraChannel } from "@app/main/components/videos/models/ICameraChannel";
import { CameraChannelService } from "@app/main/modules/cameras/_services/camera-channels.service";
import { ISimStatus } from "@app/main/modules/foundation/_models/ISimStatus";
import { CommServerRequestState } from "@app/main/modules/foundation/_routes/hardware/_enums/CommServerRequestState";
import { IPeripheralMetadata } from "@app/main/modules/foundation/_routes/hardware/_models/IPeripheralMetadata";
import { CameraAppType, ICameraApp } from "@app/main/modules/foundation/_routes/rv-lite/application/_models/ICameraApp";
import { CameraAppService } from "@app/main/modules/foundation/_routes/rv-lite/application/_services/camera-app.service";
import { ICameraConfiguration } from "@app/main/modules/foundation/_routes/rv-lite/config/_models/ICameraConfiguration";
import { ConfigManagerService } from "@app/main/modules/foundation/_routes/rv-lite/config/_services/config.service";
import { PeripheralService } from "@app/main/modules/foundation/_services/peripheral.service";
import { SimService } from "@app/main/modules/foundation/_services/sim.service";
import { CameraCommunicationServerService } from "@app/services/camera/camera-communication-server.service";
import { IConfigurationFile } from "@app/services/cameraCommunicationServer.service";
import { DateService, DateUnit } from "@app/services/date.service";
import { MapService } from "@app/services/map.service";
import { TranslationService } from "@app/services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, of } from "rxjs";
import { CreationCode } from "../../_enums/CreationCode.enum";
import { ICameraDiagnosticRecord } from "../../_models/ICameraDiagnosticRecord";
import { IPulseRecord } from "../../_models/IPulseRecord";
import { IPulseRecordPayload } from "../../_models/IPulseRecordPayload";
import { CameraEventService } from "../../_services/camera-events.service";
import { PulseService } from "../pulse/pulse.service";
import './p100-device-details.component.scss';

@UntilDestroy()
@Component({
    selector: 'p100-device-details',
    template: require('./p100-device-details.component.html')
})
export class P100DeviceDetails implements OnInit {
    @Input() peripheral: IPeripheral;
    @Output() selectChannelsTab = new EventEmitter<void>();

    pulseRecords: IPulseRecord[];
    latestPulseRecord: IPulseRecord;
    latestData: IPulseRecordPayload;
    latestEvents: ICameraDiagnosticRecord[];
    latestGPSPacket: ICameraDiagnosticRecord;
    latestDataDelayString: string;
    configInstallDate: Date;
    updaterInstallDate: Date;
    telematicsInstallDate: Date;

    rtlConfigs: IConfigurationFile[];

    peripheralMetadata: IPeripheralMetadata;

    simStatus: ISimStatus;

    cameraConfigs: ICameraConfiguration[];
    configName: string;
    cameraApplications: ICameraApp[];

    pendingConfigRequestName: string;
    pendingTelematicsAppVersion: string;
    pendingUpdaterAppVersion: string;

    cameraChannels: ICameraChannel[];
    adas = "ADAS";
    dms = "DMS";

    ProgressColourPalette = ProgressColourPalette;

    autoUpdateEnabled: boolean;

    loading: boolean;
    showSimManager: boolean;
    showChannelManager: boolean;
    showConfigManager: boolean;
    showGpsDetail: boolean;
    ignitionOffLongerThan14Days: boolean;
    locationMarker: IGoogleMapsMarker;

    CreationCode = CreationCode;

    constructor(
        private readonly dateService: DateService,
        private readonly mapService: MapService,
        private readonly pulseService: PulseService,
        private readonly simService: SimService,
        private readonly configManagerService: ConfigManagerService,
        private readonly cameraAppService: CameraAppService,
        private readonly peripheralService: PeripheralService,
        private readonly cameraChannelService: CameraChannelService,
        private readonly cameraEventService: CameraEventService,
        private readonly cameraCommunicationServer: CameraCommunicationServerService,
        private readonly translationsService: TranslationService) { }

    ngOnInit(): void {
        this.loadData();
    }

    openChannelsTab(): void {
        this.selectChannelsTab.emit();
    }

    get allChannelsCount(): number {
        return this.cameraChannels?.length
    }

    get activeChannelCount(): number {
        return this.cameraChannels?.filter(c => c.IsActive).length ?? 0;
    }

    get hasActiveAdasChannel(): boolean {
        return this.cameraChannels?.some(c => c.IsActive && c.CameraChannelType === CameraChannelType.ADASSensor) ?? false;
    }

    get hasActiveDmsChannel(): boolean {
        return this.cameraChannels?.some(c => c.IsActive && c.CameraChannelType === CameraChannelType.DMSSensor) ?? false;
    }

    openSimManager(): void {
        this.showSimManager = true;
    }

    closeSimManager(): void {
        this.showSimManager = false;
    }

    openChannelManager(): void {
        this.showChannelManager = true;
    }

    closeChannelManager(): void {
        this.showChannelManager = false;
    }

    openConfigManager(): void {
        this.showConfigManager = true;
    }

    closeConfigManager(): void {
        this.setConfigInfo();
        this.setAppInfo();
        this.showConfigManager = false;
    }

    openGpsDetail(): void {
        this.showGpsDetail = true;
    }

    closeGpsDetail(): void {
        this.showGpsDetail = false;
    }

    onVehicleChannelsSaved(vehicle: IVehicleWithCameraMappings): void {
        this.cameraChannels = vehicle.cameraChannels;
        this.closeChannelManager();
    }

    setAutoUpdate(): void {
        this.peripheralService.setAutoUpdateProperty({ Serial: this.peripheral.Serial, AutoUpdateEnabled: this.peripheralMetadata.AutoUpdate })
            .pipe(
                untilDestroyed(this),
            ).subscribe()
    }

    //--

    private loadData(): void {
        this.loading = true;
        const recordStartDate = this.dateService.addOrSubtractTime(new Date(), -14, DateUnit.Day);
        const recordEndDate = new Date();

        const vehicleId = this.peripheral?.vehicleMapping?.VehicleId;

        const cameraChannels$ = vehicleId
            ? this.cameraChannelService.getCameraChannels({ VehicleId: vehicleId })
            : of(null);

        combineLatest([
            this.pulseService.getHistoricalPulseMessages({ Serial: this.peripheral.Serial, Start: recordStartDate, End: recordEndDate }),
            this.pulseService.getLatestPulseMessage({ Serial: this.peripheral.Serial }),
            this.configManagerService.getConfigs(false),
            this.cameraAppService.getApplicationFiles(),
            cameraChannels$,
            this.peripheralService.getPeripheralMetadata(this.peripheral.Serial),
            this.cameraEventService.getGPSCameraEvents({ Serial: this.peripheral.Serial, Start: recordStartDate, End: recordEndDate })
        ]).pipe(
            untilDestroyed(this))
            .subscribe({
                next: ([pulseMessages, latestPulse, configs, apps, channels, peripheralMetadata, events]: [IPulseRecord[], IPulseRecord, ICameraConfiguration[], ICameraApp[], ICameraChannel[], IPeripheralMetadata, ICameraDiagnosticRecord[]]) => {
                    this.cameraConfigs = configs;
                    this.pulseRecords = pulseMessages;
                    this.cameraChannels = channels;
                    this.cameraApplications = apps;
                    this.peripheralMetadata = peripheralMetadata;
                    this.autoUpdateEnabled = this.peripheralMetadata.AutoUpdate;
                    this.latestEvents = events;

                    this.latestPulseRecord = latestPulse[0];
                    this.latestData = this.latestPulseRecord?.Payload ? JSON.parse(this.latestPulseRecord.Payload) : null;

                    if (!!this.latestData) {
                        this.setConfigInfo();
                        this.setAppInfo();
                        this.getIgnitionStatus();

                        this.latestGPSPacket = this.getLatestGPSPacket();
                        this.latestDataDelayString = this.computeLatestDataDelay(this.latestGPSPacket);
                        this.locationMarker = this.getLocation();
                    }

                    this.loading = false;
                }
            })
    }

    private computeLatestDataDelay(cameraDiagnosticRecord: ICameraDiagnosticRecord): string {

        let creationTime: Date = null;
        let eventTime: Date = null;

        if (!!this.latestGPSPacket) {
            creationTime = this.latestGPSPacket?.CreatedTime;
            eventTime = this.latestGPSPacket?.EventTime;
        } else if (!!this.latestData) {
            creationTime = this.latestPulseRecord.CreatedTime;
            eventTime = this.latestData.GPS.dateTime;
        }

        if (!creationTime || !eventTime) {
            return this.translationsService.t("FoundationPeripherals.NoDataDelayPacket");
        }

        const delayMs = new Date(creationTime).getTime() - new Date(eventTime).getTime();
        return this.dateService.formatTimeFromMilliseconds(delayMs);
    }

    private setConfigInfo(): void {
        this.setConfigName();
        this.setLatestConfigInstallDate();
        this.setPendingConfigRequests();
    }

    private setAppInfo(): void {
        this.setLatestAppInstalls();
        this.setPendingAppInstalls();
    }

    private getLatestGPSPacket(): ICameraDiagnosticRecord {
        if (!this.latestEvents.length) {
            return;
        }

        const latestGPSPacket = this.latestEvents.reduce((prev, current) => {
            if (new Date(prev.EventTime).getTime() < new Date(current.EventTime).getTime()) {
                return current;
            }

            return prev;

        }, this.latestEvents[0]);

        return latestGPSPacket;
    }

    private getLocation(): IGoogleMapsMarker {
        if (!this.latestGPSPacket) {
            return;
        }

        const gpsData = this.latestGPSPacket.parsedPayload.GPS;

        return this.mapService.createMapMarker({ Latitude: gpsData.latitude, Longitude: gpsData.longitude }, { label: this.dateService.formatDate(gpsData.dateTime), showLabel: true });
    }

    private getIgnitionStatus(): void {
        if (!this.latestData || this.latestData.ignitionStatus) {
            return;
        }

        const fourteenDaysAgo = this.dateService.addOrSubtractTime(new Date(), -14, DateUnit.Day);
        this.ignitionOffLongerThan14Days = this.dateService.date1IsAfterDate2(fourteenDaysAgo, this.latestData.LastIgnitionChangeTime);
    }

    private getLatestRecord(): IPulseRecord | undefined {
        return this.pulseRecords.reduce((mostRecent, current) => {
            if (!mostRecent || current.CreatedTime > mostRecent.CreatedTime) {
                return current;
            }

            return mostRecent;
        }, undefined);
    }

    private setPendingAppInstalls(): void {
        const pendingUpdaterInstalls = this.peripheral.veriAppUpdateHistory?.filter(a => (a.RequestState === CommServerRequestState.Pending || a.RequestState === CommServerRequestState.Requested) && this.cameraAppService.getAppType(a.ApplicationId, this.cameraApplications) === CameraAppType.Updater);
        const pendingTelematicsInstalls = this.peripheral.veriAppUpdateHistory?.filter(a => (a.RequestState === CommServerRequestState.Pending || a.RequestState === CommServerRequestState.Requested) && this.cameraAppService.getAppType(a.ApplicationId, this.cameraApplications) === CameraAppType.Telematics);

        if (pendingTelematicsInstalls?.length) {
            pendingTelematicsInstalls.sort((a, b) => {
                return new Date(b.CreatedTime).getTime() - new Date(a.CreatedTime).getTime();
            });

            this.pendingTelematicsAppVersion = `v${this.cameraApplications.find(app => app.Id == pendingTelematicsInstalls[0].ApplicationId).Version}`;
        }

        if (pendingUpdaterInstalls?.length) {
            pendingUpdaterInstalls.sort((a, b) => {
                return new Date(b.CreatedTime).getTime() - new Date(a.CreatedTime).getTime();
            });

            this.pendingUpdaterAppVersion = `v${this.cameraApplications.find(app => app.Id == pendingUpdaterInstalls[0].ApplicationId).Version}`;
        }
    }

    private setLatestAppInstalls(): void {
        if (!this.peripheral.veriAppUpdateHistory?.length) {
            return;
        }

        const updaterInstalls = this.peripheral.veriAppUpdateHistory.filter(a => a.RequestState === CommServerRequestState.Completed && this.cameraAppService.getAppType(a.ApplicationId, this.cameraApplications) === CameraAppType.Updater);
        const telematicsInstalls = this.peripheral.veriAppUpdateHistory.filter(a => a.RequestState === CommServerRequestState.Completed && this.cameraAppService.getAppType(a.ApplicationId, this.cameraApplications) === CameraAppType.Telematics);

        if (!!updaterInstalls.length) {
            updaterInstalls.sort((a, b) => {
                return new Date(b.SentTime).getTime() - new Date(a.SentTime).getTime();
            });

            this.updaterInstallDate = updaterInstalls[0].SentTime;
        }

        if (!!telematicsInstalls.length) {
            telematicsInstalls.sort((a, b) => {
                return new Date(b.SentTime).getTime() - new Date(a.SentTime).getTime();
            });

            this.telematicsInstallDate = telematicsInstalls[0].SentTime;
        }
    }

    private setLatestConfigInstallDate(): void {
        if (this.peripheral.veriConfigUpdateHistory?.length) {
            const completedRequests = this.peripheral.veriConfigUpdateHistory.filter(c => c.RequestState === CommServerRequestState.Completed);

            completedRequests.sort((a, b) => {
                return new Date(b.SentTime).getTime() - new Date(a.SentTime).getTime();
            });

            this.configInstallDate = completedRequests[0]?.SentTime;
            return;
        }

        // Remove with CommServer
        if (this.peripheral?.rtlConfigUpdateHistory?.length) {
            const relevantRequests = this.peripheral.rtlConfigUpdateHistory.filter(c => c.Description == this.configName);

            if (!relevantRequests.length) {
                return;
            }

            relevantRequests.sort((a, b) => {
                return new Date(b.RequestedDate).getTime() - new Date(a.RequestedDate).getTime();
            });

            this.configInstallDate = relevantRequests[0].InstalledDate ?? relevantRequests[0].RequestedDate;
        }
    }

    private setPendingConfigRequests(): void {
        const pendingVerilocationRequests = this.peripheral.veriConfigUpdateHistory?.filter(c => c.RequestState === CommServerRequestState.Pending || c.RequestState === CommServerRequestState.Requested);

        if (pendingVerilocationRequests?.length) {
            pendingVerilocationRequests.sort((a, b) => {
                return new Date(b.CreatedTime).getTime() - new Date(a.CreatedTime).getTime();
            });

            this.pendingConfigRequestName = this.cameraConfigs.find(config => config.Id == pendingVerilocationRequests[0].ConfigurationId).Name;
            return;
        }

        // Remove with CommServer
        const pendingRtlRequests = this.peripheral.rtlConfigUpdateHistory?.filter(c => !c.InstalledDate && c.Description != this.configName && c.Description != "Verilocation_Migration_Config");

        if (pendingRtlRequests?.length) {
            pendingRtlRequests.sort((a, b) => {
                return new Date(b.RequestedDate).getTime() - new Date(a.RequestedDate).getTime();
            });

            this.pendingConfigRequestName = pendingRtlRequests[0].Description;
            return;
        }

    }
    private setConfigName(): void {
        if (this.latestData?.HardwareConfigName.indexOf(';') > -1) {
            this.latestData.HardwareConfigName = this.latestData.HardwareConfigName.substring(0, this.latestData?.HardwareConfigName.indexOf(';'))
        }

        const pulseConfigName = this.pulseService.extractGuid(this.latestData?.HardwareConfigName);

        // If pulseConfigName is not in the mapping dictionary
        if (!pulseConfigName) {
            this.configName = this.rtlConfigs?.find(
                c => c.ConfigFileUniqueId?.toUpperCase() === pulseConfigName?.toUpperCase()
            )?.ConfigFileName;
            return;
        }

        this.configName = this.cameraConfigs.find(config => config.Id === pulseConfigName)?.Name ?? this.commServerToVerilocationConfigMappings[pulseConfigName] ?? "Unkown";
    }

    private commServerToVerilocationConfigMappings: { [key: string]: string } = {
        "C157A7C1-723D-408C-B7D9-32D27DBDE60D": "NEEDS TO BE MAPPED",
        "b2258a1a-168e-466b-ae23-6dfac432624f": "NEEDS TO BE MAPPED"
    };
}

